"use client";
import { Dispatch, createContext, useContext, useReducer } from "react";

export const AvatarPlaceholder = (firstName: string, lastName: string) =>
  `https://api.dicebear.com/9.x/initials/svg?seed=${firstName + lastName}&radius=50&backgroundColor=000000&textColor=FFFFFF`;
export const AWSImage = (cognitoUsername: string, version: number) =>
  `https://app-profile-pictures.s3.amazonaws.com/${cognitoUsername}.jpg?v=${version}`;

export type ProfilePictureDispatcherActions =
  | { type: "id"; id: string }
  | { type: "update-image" }
  | { type: "set-image-url"; imageUrl: string }
  | { type: "clear-image" }
  | { type: "name"; firstName: string; lastName: string }
  | { type: "init"; id: string; firstName: string; lastName: string }
  | { type: "hasPicture"; hasPicture: boolean };

export type ProfilePictureContextState = {
  version: number;
  id: string;
  firstName: string;
  lastName: string;
  hasPicture: boolean;
  imageUrl?: string | null;
};

export type ProfilePictureHook = {
  state: ProfilePictureContextState;
  dispatch: Dispatch<ProfilePictureDispatcherActions>;
};

const initialState: ProfilePictureContextState = {
  id: "0",
  version: 0,
  firstName: "John",
  lastName: "Smith",
  hasPicture: true,
  imageUrl: null,
};

export const profilePictureReducer = (
  current: ProfilePictureContextState,
  action: ProfilePictureDispatcherActions,
) => {
  const copy = { ...current };
  switch (action.type) {
    case "update-image":
      copy.version = new Date().getTime();
      break;
    case "set-image-url":
      copy.imageUrl = action.imageUrl;
      copy.version = new Date().getTime();
      break;
    case "clear-image":
      // Drop any optimistic/local image URL and bump the buster so the
      // avatar reloads the current S3 object (no-cache) on next render.
      copy.imageUrl = null;
      copy.version = new Date().getTime();
      break;
    case "name":
      copy.firstName = action.firstName;
      copy.lastName = action.lastName;
      break;
    case "init":
      copy.id = action.id;
      copy.firstName = action.firstName;
      copy.lastName = action.lastName;
      // Fresh cache-buster per load so a hard refresh fetches the current
      // S3 object instead of the browser's stale `?v=0` copy.
      copy.version = new Date().getTime();
      break;
    case "hasPicture":
      copy.hasPicture = action.hasPicture;
      break;
  }
  return copy;
};
export const ProfilePictureContext = createContext<ProfilePictureHook | null>(
  null,
);
export function ProfilePictureContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(profilePictureReducer, initialState);
  // To re-enable profile photo nudges:
  // 1) add `useEffect` to the react import
  // 2) add `toast` and `Slide` from "react-toastify"
  // 3) uncomment the block below
  //
  // useEffect(() => {
  //   if (state.hasPicture === false) {
  //     const toastId = "account-incomplete";
  //     if (toast.isActive(toastId)) {
  //       return;
  //     }
  //     toast("✨ Welcome! Update profile photo to send invites.", {
  //       toastId,
  //       position: "top-center",
  //       hideProgressBar: true,
  //       theme: "dark",
  //       autoClose: 7000,
  //       transition: Slide,
  //     });
  //   }
  // }, [state.hasPicture]);
  return (
    <ProfilePictureContext.Provider value={{ state, dispatch }}>
      {children}
    </ProfilePictureContext.Provider>
  );
}

export function useProfilePictureContext() {
  const context = useContext(ProfilePictureContext);
  if (context === null) {
    throw new Error("Missing profile picture context");
  }
  return context;
}
